import React, { useState, useEffect, useRef } from 'react';
import { coursePackagesData } from '../data/courseData';
import CoursePackageTile from '../components/CoursePackageTile.js';
import CourseDetailsSection from '../components/CourseDetailsSection.js';
import PathwaysSection from '../components/PathwaysSection.js';
import './LandingPage.css';

function LandingPage({ onEnrollNow }) {
    const [selectedCourse, setSelectedCourse] = useState(null);
    const detailsSectionRef = useRef(null);

    const handleCourseSelect = (course) => {
        if (selectedCourse && selectedCourse.id === course.id) {
            setSelectedCourse(null);
        } else {
            setSelectedCourse(course);
        }
    };

    const handleCloseDetails = () => {
        setSelectedCourse(null);
    };

    const handleEnrollNow = (product) => {
        if (onEnrollNow) {
            onEnrollNow(product);
        }
    };

    useEffect(() => {
        if (selectedCourse && detailsSectionRef.current) {
            detailsSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [selectedCourse]);

    return (
        <div className="page-wrapper">
            {/* --- 1. NEW HERO SECTION --- */}
            <header className="hero-section-split">
                <div className="hero-container">

                    {/* Left: Text */}
                    <div className="hero-text-content">
                        <div className="hero-badge">For Educators & Tutors</div>
                        <h1>Adult Literacy & Numeracy</h1>
                        <p className="hero-description">
                            Equip your team with evidence-informed strategies to build confidence and capability in your learners.
                        </p>

                        <div className="hero-primary-action">
                            <button
                                className="btn-consultation-large"
                                onClick={() => handleEnrollNow('Free Consultation')}
                            >
                                Book Your Free Consultation
                                <span className="btn-icon">→</span>
                            </button>
                            <p className="hero-subtext">No commitment. Speak with Dr Damon Whitten.</p>
                        </div>
                    </div>

                    {/* Right: Video */}
                    <div className="hero-media-content">
                        <div className="video-wrapper">
                            <video
                                poster="https://discoverylearningchat-public-media.s3.ap-southeast-2.amazonaws.com/pic2x2_blue_6_9.png"
                                src="https://adultliteracyandnumeracy-public-media.s3.ap-southeast-2.amazonaws.com/videos/welcome_to_the_website2.mp4"
                                controls
                                playsInline
                                preload="metadata"
                            />
                        </div>
                        <p className="video-caption">Dr Damon Whitten: Specialist in Adult Literacy & Numeracy</p>
                    </div>
                </div>
            </header>

            {/* --- 2. DISCOVERY BAR --- */}
            <div className="course-discovery-bar">
                <div className="discovery-content">
                    <span className="discovery-label">Looking for professional development resources?</span>
                    <div className="discovery-buttons">
                        <button
                            className="btn-text-link"
                            onClick={() => document.getElementById('course-pathways-section').scrollIntoView({ behavior: 'smooth' })}
                        >
                            Explore Pathways
                        </button>
                        <span className="divider">|</span>
                        <button
                            className="btn-text-link"
                            onClick={() => document.getElementById('course-packages-section').scrollIntoView({ behavior: 'smooth' })}
                        >
                            View Short Courses
                        </button>
                    </div>
                </div>
            </div>

            {/* --- 3. INTRO BLOCKS --- */}
            <section className="intro-section">
                <div className="hero-content-wrapper">
                    <div className="intro-header">
                        <h3>Empowering Educators</h3>
                        <p>Evidence-informed strategies that work in the real world.</p>
                    </div>

                    <div className="hero-intro-blocks-container">
                        <div className="intro-block">
                            <h2>The Challenge</h2>
                            <p>Many adult learners have the potential to succeed in vocational training, but gaps in literacy and numeracy hold them back. Without the right tools, educators struggle to build the confidence and capability learners need.</p>
                        </div>
                        <div className="intro-block">
                            <h2>Our Solution</h2>
                            <p>We provide evidence-informed professional learning that equips educators with practical strategies. Our approach blends research with hands-on tools that tutors can use immediately.</p>
                        </div>
                        <div className="intro-block">
                            <h2>Our Commitment</h2>
                            <ul className="offer-list">
                                <li>Strengthen learner outcomes through practical strategies.</li>
                                <li>Grow educator capability so teaching becomes easier.</li>
                                <li>Partner with an experienced leader in adult education.</li>
                                <li>Leverage 25 years of research-based, high-impact strategies.</li>
                                <li>Bring the joy back to adult education.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section id="course-pathways-section" style={{ scrollMarginTop: "100px" }}>
                <PathwaysSection handleEnrollNow={handleEnrollNow} />
            </section>

            <section id="course-packages-section" className="course-packages-section">
                <h2>Short Course Packages</h2>
                <p className="section-intro">Prefer to focus on a specific area? Choose any of our course packages individually.</p>
                <div className="course-tiles-grid">
                    {coursePackagesData.map(course => (
                        <CoursePackageTile
                            key={course.id}
                            id={`course-tile-${course.id}`}
                            course={course}
                            onSelect={handleCourseSelect}
                            isSelected={selectedCourse && selectedCourse.id === course.id}
                        />
                    ))}
                </div>
            </section>

            {selectedCourse && (
                <CourseDetailsSection
                    course={selectedCourse}
                    onClose={handleCloseDetails}
                    detailsSectionRef={detailsSectionRef}
                    handleEnrollNow={handleEnrollNow}
                />
            )}
        </div>
    );
}

export default LandingPage;